import React, {useEffect, useRef} from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import {StringParam, useQueryParam} from 'use-query-params';
import useStyles from '../common/FormTextField/FormTextField.styles';
import {useStrings} from '../../utils/localization';
import {
  cleanPhoneInput,
  isValidNumber,
  CountryType,
  getCountries,
  defaultCountry,
} from './InternationalPhone.service';
import {useFeature} from '@growthbook/growthbook-react';
import {FEATURE_IDS} from '../../utils/featureFlags';
import InputAdornment from '@mui/material/InputAdornment';

type Props = {
  onValidationChanged: (isValid: boolean) => void;
};

export const InternationalPhone = ({onValidationChanged}: Props) => {
  const [countryCodeQuery, setCountryCodeQuery] = useQueryParam(
    'countryCode',
    StringParam,
  );

  const expansionCountries = useFeature(
    FEATURE_IDS.ONBOARDING_INTERNATIONAL_EXPANSION_COUNTRIES,
  );

  const countries = getCountries(expansionCountries.value);

  const [phoneQuery, setPhoneQuery] = useQueryParam('phone', StringParam);
  const defaultSelected =
    countries.find(country => country.code === countryCodeQuery) ||
    defaultCountry;
  const [countryInput, setCountryInput] = React.useState<string>('');
  const [selectedCountry, setSelectedCountry] =
    React.useState<CountryType>(defaultSelected);
  const defaultPhone =
    phoneQuery?.replace(`+${selectedCountry.phone}`, '') || '';
  const [phoneInput, setPhoneInput] = React.useState<string>(defaultPhone);
  const [phoneError, setPhoneError] = React.useState<boolean>(false);

  const lastValidationResult = useRef<boolean | null>(null);

  useEffect(() => {
    const cleanInput = cleanPhoneInput(phoneInput);
    const phoneWithCode = `+${selectedCountry.phone}${cleanInput}`;
    const valid = isValidNumber(phoneWithCode);

    if (lastValidationResult.current !== valid) {
      onValidationChanged(valid);
      lastValidationResult.current = valid;
    }
  }, [phoneInput, selectedCountry, onValidationChanged]);

  const classes = useStyles();
  const strings = useStrings();

  const onInput = (event: any) => {
    const input = event.target.value;
    setPhoneInput(input);
    const cleanInput = cleanPhoneInput(input);
    const phoneWithCode = `+${selectedCountry.phone}${cleanInput}`;

    const isValidPhone = isValidNumber(phoneWithCode);
    if (!isValidPhone) {
      onValidationChanged(false);
      return;
    }
    onValidationChanged(true);
    setPhoneError(false);
    setPhoneQuery(phoneWithCode);
    window.analytics.identify({
      tel: phoneWithCode,
    });
  };

  const errorCheck = () => {
    const cleanInput = cleanPhoneInput(phoneInput);
    const phoneWithCode = `+${selectedCountry.phone}${cleanInput}`;
    setPhoneError(!isValidNumber(phoneWithCode));
  };

  const onBlur = () => {
    errorCheck();
  };

  return (
    <Box className={classes.fieldContainer}>
      <Box>
        <Autocomplete
          options={countries}
          value={selectedCountry}
          inputValue={countryInput}
          getOptionLabel={option => `${option.label} (+${option.phone})`}
          onInputChange={(event, newInputValue) => {
            setCountryInput(newInputValue);
          }}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          onChange={(event, newValue) => {
            if (newValue) {
              setSelectedCountry(newValue);
              setCountryCodeQuery(newValue.code);
              const cleanInput = cleanPhoneInput(phoneInput);
              setPhoneQuery(`+${newValue.phone}${cleanInput}`);
            }
          }}
          renderInput={params => {
            return (
              <TextField
                {...params}
                label="Country/Region"
                variant="outlined"
                className={classes.textField}
              />
            );
          }}
        />
      </Box>

      <Box className={classes.intlPhoneField}>
        <TextField
          variant="outlined"
          value={phoneInput}
          type="tel"
          label={phoneError ? strings.invalidPhone : strings.phoneNumber}
          error={phoneError}
          style={{width: '100%'}}
          className={classes.textField}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  color: 'text.primary',
                }}>
                +{selectedCountry.phone}
              </InputAdornment>
            ),
          }}
          onInput={onInput}
          onBlur={onBlur}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              errorCheck();
              onInput(event);
            }
          }}
        />
      </Box>
    </Box>
  );
};
